import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material"
import { useMemo, useState } from "react"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { Bound } from "lib/@components/binding/Bound"
import { useRefresh } from "lib/@hooks/useRefresh"
import { invalidate } from "lib/graphql/cache"
import { reprocessMaintenanceCategory } from "../../controller/maintenance-library"
import { useSnackbar } from "notistack"

export function SourceEditorDialog({ open, onClose, category }) {
    const [processing, setProcessing] = useState(false)
    const refresh = useRefresh()
    const { enqueueSnackbar } = useSnackbar()

    const target = useMemo(() => {
        if (!category) return null
        return { text: category.originalText || "" }
    }, [category, open])

    if (!category) return null

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <Bound target={target} refresh={refresh}>
                <DialogTitle>Edit Original Source Text</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Modify the source text below. Saving will re-run the AI extraction and update the components
                            for this file.
                        </Typography>
                        <BoundTextField
                            field="text"
                            label="Source Text"
                            multiline
                            rows={15}
                            autoFocus
                            sx={{ "& textarea": { fontFamily: "monospace", fontSize: "0.875rem" } }}
                        />
                        {processing && (
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                                <CircularProgress size={24} />
                                <Typography variant="body2">Extracting and ingesting components...</Typography>
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={processing}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" disabled={processing} onClick={handleSave}>
                        Save and Reprocess
                    </Button>
                </DialogActions>
            </Bound>
        </Dialog>
    )

    function handleClose() {
        if (!processing) {
            onClose()
        }
    }

    async function handleSave() {
        setProcessing(true)
        try {
            await reprocessMaintenanceCategory(category.id, target.text)
            // Invalidate the category query to force a refetch with updated tasks
            invalidate("getMaintenanceCategory")
            onClose()
        } catch (error) {
            console.error("reprocessMaintenanceCategory failed", error)
            enqueueSnackbar(getErrorMessage(error), { variant: "error" })
        } finally {
            setProcessing(false)
        }
    }

    function getErrorMessage(error) {
        const graphQlMessage = error?.graphQLErrors?.[0]?.message
        if (graphQlMessage) return graphQlMessage
        if (error?.message) return error.message
        return "Reprocessing failed. Please check the source text format and try again."
    }
}
